import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { PDFDocument, rgb } from 'pdf-lib';

interface BiltyData {
  lrNumber: string;
  bookingDate: string;
  invoiceNo: string;
  invoiceValue: string;
  eWayBillNo: string;
  privateMarka: string;
  consignorCompanyName: string;
  consignorGST: string;
  consigneeCompanyName: string;
  consigneeGST: string;
  fromCity: string;
  toCity: string;
  description: string;
  noOfArticles: string;
  actualWeight: string;
  chargedWeight: string;
  pricePerKg: string;
  totalFreightCharges: string;
  paymentBy: string;
  deliveryType: string;
  copyType: string;
  partnerGTA: string;
  orderMode: 'PTL' | 'FTL';
  signatureImage?: string; // Base64 signature image
}

const formatValue = (value: string | number): string => {
  if (value === '-1' || value === -1) {
    return 'N.A';
  }
  return String(value);
};

const formatCost = (value: string | number): string => {
  if (value === '-1' || value === -1) {
    return 'PAID';
  }
  return String(value);
};

const formatFreight = (value: string | number): string => {
  if (value === '-1' || value === -1) {
    return 'PAID';
  }
  return String(value);
};

export const buildBiltyHTML = (data: BiltyData): string => {
  return `
<!DOCTYPE html>
 <html>
 <head>
  <meta charset="utf-8">
  <title>Premium Consignment Note</title>
  <link href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap" rel="stylesheet">
  <link href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0/css/all.min.css" rel="stylesheet">
  <style>
    * {
      -webkit-print-color-adjust: exact !important;
      print-color-adjust: exact !important;
      color-adjust: exact !important;
      box-sizing: border-box;
    }
    :root {
      --primary: #3498db;
      --secondary: #1f6aa7;
      --accent: #2ecc71;
      --success: #27ae60;
      --warning: #f1c40f;
      --danger: #e74c3c;
      --dark: #1a2b3c;
      --text: #2c3e50;
      --text-muted: #7f8c8d;
      --border: #bdc3c7;
      --background: #f8f9fb;
      --surface: #ffffff;
    }
    body {
      font-family: 'Inter', sans-serif;
      margin: 0;
      padding: 0px;
      background: #ffffff;
    }
    .container {
      background: white;
      border-radius: 20px;
      padding: 9px 30px 9px 30px;
      position: relative;
      overflow: hidden;
      max-width: 1100px;
      margin: 0 auto;
    }
    .section-title {
      font-size: 15px;
      font-weight: 700;
      color: var(--dark);
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .price-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 8px 12px;
      background: rgba(255, 255, 255, 0.1);
      border-radius: 6px;
    }
    .total-amount {
      font-size: 26px;
      font-weight: 800;
      margin: 8px 0;
    }
    .terms-section {
      border-top: 2px solid #e2e8f0;
      padding-top: 18px;
      margin-top: 20px;
      font-size: 12px;
      color: #64748b;
      line-height: 1.5;
    }
    .financial-section {
      display: flex;
      justify-content: space-between;
      align-items: center;
      background: #f8fafc;
      padding: 1.25rem;
      border-radius: 8px;
      border: 2px solid #e2e8f0;
      margin: 1.5rem 0;
    }
    .price-details {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 1rem;
      flex-grow: 1;
    }
    .total-freight {
      text-align: right;
      padding-left: 1.5rem;
      border-left: 2px solid var(--primary);
      margin-left: 1.5rem;
    }
    .section-divider {
      border-bottom: 2px solid var(--primary);
      margin: 1.2rem 0 1.2rem 0;
      opacity: 0.1;
    }
    .header {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      padding: 1rem 1.5rem;
      background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
      border-radius: 12px;
      margin-bottom: 1.5rem;
      position: relative;
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }
    .brand-section {
      display: flex;
      align-items: center;
      gap: 1.2rem;
      flex: 1;
    }
    .logo {
      width: 4rem;
      height: 4rem;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      backdrop-filter: blur(4px);
      border: 1px solid rgba(255, 255, 255, 0.2);
    }
    .logo-icon {
      color: white;
      font-size: 1.8rem;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
    }
    .company-info {
      display: flex;
      flex-direction: column;
      gap: 0.4rem;
    }
    .company-name {
      font-size: 1.4rem;
      font-weight: 700;
      color: white;
      letter-spacing: -0.5px;
      text-shadow: 0 2px 4px rgba(0,0,0,0.1);
    }
    .legal-info {
      font-size: 0.72rem;
      color: rgba(255,255,255,0.85);
      line-height: 1.3;
      margin-top: 2px;
    }
    .document-meta {
      position: absolute;
      right: 0;
      top: 0px;
      text-align: right;
    }
    .copy-badge {
      background: var(--accent);
      color: white;
      padding: 0.5rem 1.5rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      font-weight: 700;
      display: inline-block;
    }
    .contact-info {
      display: flex;
      flex-direction: column;
      gap: 0.3rem;
      margin-top: 1.6rem;
      padding-right: 0.5rem;
    }
    .contact-item {
      display: flex;
      gap: 0.5rem;
      align-items: center;
      font-size: 0.65rem;
      color: white;
      opacity: 0.9;
      transition: opacity 0.2s ease;
    }
    .contact-item:hover {
      opacity: 1;
    }
    .contact-icon {
      color: rgba(255,255,255,0.9);
      font-size: 0.9rem;
    }
    .meta-grid {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 12px;
      margin-bottom: 20px;
    }
    .meta-itemnew {
      padding: 12px;
      background: #f8f9fb;
      border-radius: 8px;
      border: 2px solid #e2e8f0;
      border-left: 3px solid var(--primary);
    }
    .meta-label {
      font-size: 12px;
      color: #64748b;
      margin-bottom: 4px;
    }
    .meta-item {
      padding: 12px;
      background: #f1f5f9;
      border-radius: 8px;
    }
    .meta-valuenew {
      font-size: 0.9rem;
      font-weight: 700;
      color: var(--text);
    }
    .meta-value {
      font-size: 14px;
      font-weight: 600;
      color: var(--dark);
    }
    .meta-valuecompany {
      font-size: 14px;
      font-weight: 600;
      color: var(--dark);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      max-width: 100%;
      display: block;
    }
    .parties-gridv > * {
      min-width: 0;
    }
    .meta-valuedelivery {
      font-size: 14px;
      font-weight: 600;
      color: var(--dark);
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .location-bar {
      display: grid;
      grid-template-columns: 1fr auto 1fr;
      align-items: center;
      background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
      padding: 8px 30px;
      border-radius: 10px;
      margin: 1rem 0;
      min-width: 0;
      overflow: hidden;
    }
    .location-bar > div {
      min-width: 0;
      padding: 0 15px;
      display: flex;
      flex-direction: column;
      align-items: center;
    }
    .location-bar i {
      font-size: 1.5rem;
      opacity: 0.9;
      margin: 0 10px;
      color: rgba(255, 255, 255, 0.9);
      filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2));
    }
    .location-city {
      font-size: 1.1rem;
      font-weight: 700;
      letter-spacing: 0.5px;
      margin-top: 4px;
      color: #ffffff !important;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      max-width: 100%;
      width: 100%;
      box-sizing: border-box;
      text-align: center;
    }
    .location-label {
      font-size: 0.75rem;
      text-transform: uppercase;
      opacity: 0.9;
      margin-bottom: 2px;
      color: rgba(255, 255, 255, 0.9) !important;
      font-weight: 600;
      letter-spacing: 1px;
    }
    .parties-grid {
      display: grid;
      grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
      gap: 20px;
      margin: 20px 0;
    }
    .party-card {
      padding: 18px;
      border: 2px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }
    .delivery-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
      margin: 20px 0;
    }
    .shipment-grid {
      display: grid;
      grid-template-columns: repeat(4, minmax(0, 1fr));
      gap: 12px;
      margin: 20px 0;
    }
    .shipment-item {
      padding: 12px;
      background: #f8fafc;
      border-radius: 8px;
      min-width: 0;
    }
  </style>
 </head>
 <body>
   <div class="container">
     <div class="header">
       <div class="brand-section">
         <div class="logo">
           <i class="fas fa-parachute-box logo-icon"></i>
         </div>
         <div class="company-info">
           <div class="company-name">Instaship Logistics Private Ltd</div>
           <div class="legal-info">
             Ground Floor, 10/14 Old Rajinder Nagar,<br>
             New Delhi, Delhi - 110060<br>
             Transporter ID: 07AAICI1317H1ZS
           </div>
         </div>
       </div>
       <div class="document-meta">
         <div class="copy-badge">${"Consignor"}</div>
         <div class="contact-info">
           <div class="contact-item">
             <i class="fas fa-phone-alt contact-icon"></i>
             +91-8360552876
           </div>
           <div class="contact-item">
             <i class="fas fa-envelope contact-icon"></i>
             support@instaship.co.in
           </div>
         </div>
       </div>
     </div>

     <div class="meta-grid">
       <div class="meta-itemnew">
         <div class="meta-label">LR NUMBER</div>
         <div class="meta-value">${data.lrNumber}</div>
       </div>
       <div class="meta-itemnew">
         <div class="meta-label">E-WAY BILL NO</div>
         <div class="meta-value">${formatValue(data.eWayBillNo)}</div>
       </div>
       <div class="meta-itemnew">
         <div class="meta-label">INVOICE NO</div>
         <div class="meta-value">${formatValue(data.invoiceNo)}</div>
       </div>
       <div class="meta-itemnew">
         <div class="meta-label">INVOICE VALUE</div>
         <div class="meta-value">${formatValue(data.invoiceValue)}</div>
       </div>
     </div>

     <div class="location-bar">
       <div>
         <div class="location-label">Origin</div>
         <div class="location-city">${data.fromCity}</div>
       </div>
       <i class="fas fa-arrow-right"></i>
       <div>
         <div class="location-label">Destination</div>
         <div class="location-city">${data.toCity}</div>
       </div>
     </div>

     <div class="parties-grid">
       <div class="party-card">
         <div class="section-title">
           <i class="fas fa-user-tie"></i>
           Consignor Details
         </div>
         <div class="meta-item" style="background:none;padding:0;margin-bottom:12px">
           <div class="meta-label">Company Name</div>
           <div class="meta-valuecompany">${data.consignorCompanyName}</div>
         </div>
         <div class="meta-item" style="background:none;padding:0">
           <div class="meta-label">GST Number</div>
           <div class="meta-value">${data.consignorGST}</div>
         </div>
       </div>

       <div class="party-card">
         <div class="section-title">
           <i class="fas fa-building"></i>
           Consignee Details
         </div>
         <div class="meta-item" style="background:none;padding:0;margin-bottom:12px">
           <div class="meta-label">Company Name</div>
           <div class="meta-valuecompany">${data.consigneeCompanyName}</div>
         </div>
         <div class="meta-item" style="background:none;padding:0">
           <div class="meta-label">GST Number</div>
           <div class="meta-value">${data.consigneeGST}</div>
         </div>
       </div>
     </div>

     <div class="section-title">
       <i class="fas fa-box-open"></i>
       Shipment Details
     </div>
     <div class="shipment-grid">
       <div class="shipment-item">
         <div class="meta-label">Description</div>
         <div class="meta-valuedelivery">${data.description}</div>
       </div>
       <div class="shipment-item">
         <div class="meta-label">Articles</div>
         <div class="meta-value">${formatValue(data.noOfArticles)}</div>
       </div>
       <div class="shipment-item">
         <div class="meta-label">Actual Weight (Kg)</div>
         <div class="meta-value">${formatValue(data.actualWeight)}</div>
       </div>
      <div class="shipment-item">
         <div class="meta-label">Charged Weight (Kg)</div>
         <div class="meta-value">${formatValue(-1)}</div>
       </div>
     </div>

     <div class="section-divider"></div>

     <div class="section-title">
       <i class="fas fa-truck-fast"></i>
       Delivery Information
     </div>
     <div class="delivery-grid">
       <div class="shipment-item">
         <div class="meta-label">Booking Date</div>
         <div class="meta-value">${data.bookingDate}</div>
       </div>
       <div class="shipment-item">
         <div class="meta-label">Delivery Type</div>
         <div class="meta-value">${data.deliveryType}</div>
       </div>
       <div class="shipment-item">
         <div class="meta-label">Payment By</div>
         <div class="meta-value">${data.paymentBy}</div>
       </div>
       <div class="shipment-item">
         <div class="meta-label">${data.orderMode === 'FTL' ? 'Vehicle No' : 'Partner GTA'}</div>
         <div class="meta-value">${data.partnerGTA}</div>
       </div>
     </div>

     <div class="financial-section">
       <div class="price-details">
         <div class="meta-item">
           <div class="meta-label">TRANSPORTATION COST</div>
           <div class="meta-value highlight">${formatCost(-1)}</div>
         </div>
         <div class="meta-item">
           <div class="meta-label">PROCESSING FEE</div>
           <div class="meta-value highlight">${formatCost(data.pricePerKg)}</div>
         </div>
       </div>
       <div class="total-freight">
         <div class="meta-label">TOTAL FREIGHT</div>
         <div class="total-amount">${formatFreight(-1)}</div>
       </div>
     </div>

     <div class="terms-section">
       <strong>Important Notes:</strong><br>
       1. Goods once delivered will not be taken back<br>
       2. Freight charges are non-refundable<br>
       3. Damage claims must be reported within 24 hours<br>
       4. GST is payable under Reverse Charge Mechanism (RCM) as applicable<br>
     </div>
   </div>
 </body>
 </html>
`;
};

/**
 * Generates a BILTY PDF from the given data
 * @param biltyData The data to generate the BILTY from
 * @param printRef The hidden div reference to render HTML before conversion
 * @returns A PDF Blob
 */
export const generateBiltyPDF = async (
  biltyData: BiltyData,
  printRef: HTMLDivElement
): Promise<Blob> => {
  const html = buildBiltyHTML(biltyData);
  printRef.innerHTML = html;
  await new Promise((resolve) => setTimeout(resolve, 500));

  const canvas = await html2canvas(printRef, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    allowTaint: true,
    scrollY: -window.scrollY,
    scrollX: -window.scrollX,
    windowWidth: printRef.scrollWidth,
    windowHeight: printRef.scrollHeight,
  });

  const imgData = canvas.toDataURL('image/png');
  const pdf = new jsPDF({
    orientation: 'p',
    unit: 'pt',
    format: 'a4',
    compress: true,
  });

  const pdfWidth = pdf.internal.pageSize.getWidth();
  const pdfHeight = pdf.internal.pageSize.getHeight();
  pdf.addImage(imgData, 'PNG', 0, 0, pdfWidth, pdfHeight);

  return pdf.output('blob');
};

/**
 * Overlay POD signature on an existing bilty PDF (matching backend implementation)
 * @param biltyPdfBlob The original bilty PDF blob
 * @param signatureBase64 Base64 signature data (with or without data URI prefix)
 * @param signerName Optional signer name
 * @param signerPhone Optional signer phone
 * @param ipAddress Optional IP address
 * @param geolocation Optional geolocation {lat, lng}
 * @returns Modified PDF blob with signature overlaid
 */
export const overlaySignatureOnBilty = async (
  biltyPdfBlob: Blob,
  signatureBase64: string,
  signerName?: string,
  signerPhone?: string,
  ipAddress?: string,
  geolocation?: { lat: number; lng: number }
): Promise<Blob> => {
  // Convert blob to array buffer
  const pdfArrayBuffer = await biltyPdfBlob.arrayBuffer();
  const pdfBytes = new Uint8Array(pdfArrayBuffer);

  // Load the PDF with pdf-lib
  const pdfDoc = await PDFDocument.load(pdfBytes);
  
  // Get the last page (typically where signature goes)
  const pages = pdfDoc.getPages();
  const lastPage = pages[pages.length - 1];
  const { width, height } = lastPage.getSize();

  // Extract base64 PNG data (remove data:image/png;base64, prefix if present)
  const base64Data = signatureBase64.includes(',')
    ? signatureBase64.split(',')[1]
    : signatureBase64;
  
  // Convert base64 to Uint8Array
  const binaryString = atob(base64Data);
  const len = binaryString.length;
  const signatureImageBytes = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    signatureImageBytes[i] = binaryString.charCodeAt(i);
  }

  // Embed the signature image
  const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

  // Prepare metadata text
  const signedDate = new Date();
  const formattedDate = signedDate.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
  const formattedTime = signedDate.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  // Stamp box — bottom-right, aligned with "Important Notes" section
  const bw = 222;
  const bh = 97;
  const bx = width - bw - 18;
  const by = 60;
  const hh = 22;   // header height

  // ── Step 1: outer border + background ────────────────────────────────────
  lastPage.drawRectangle({
    x: bx,
    y: by,
    width: bw,
    height: bh,
    color: rgb(0.965, 0.973, 0.988),
    borderColor: rgb(0.122, 0.416, 0.655),
    borderWidth: 1.4,
  });

  // ── Step 2: gradient header strips ───────────────────────────────────────
  // #3498db → #1f6aa7
  const numStrips = 36;
  const stripW = bw / numStrips;
  for (let i = 0; i < numStrips; i++) {
    const t = i / (numStrips - 1);
    lastPage.drawRectangle({
      x: bx + i * stripW,
      y: by + bh - hh,
      width: stripW + 0.5,
      height: hh,
      color: rgb(
        0.204 + (0.122 - 0.204) * t,
        0.596 + (0.416 - 0.596) * t,
        0.859 + (0.655 - 0.859) * t,
      ),
      borderWidth: 0,
    });
  }

  // Redraw top border line over gradient (gradient strips erase the top border)
  lastPage.drawLine({
    start: { x: bx, y: by + bh },
    end:   { x: bx + bw, y: by + bh },
    thickness: 1.4,
    color: rgb(0.122, 0.416, 0.655),
  });
  lastPage.drawLine({
    start: { x: bx, y: by + bh },
    end:   { x: bx, y: by },
    thickness: 1.4,
    color: rgb(0.122, 0.416, 0.655),
  });
  lastPage.drawLine({
    start: { x: bx + bw, y: by + bh },
    end:   { x: bx + bw, y: by },
    thickness: 1.4,
    color: rgb(0.122, 0.416, 0.655),
  });

  // ── Step 4: header text ───────────────────────────────────────────────────
  lastPage.drawText('DIGITALLY SIGNED POD', {
    x: bx + 8,
    y: by + bh - 14,
    size: 8.5,
    color: rgb(1, 1, 1),
  });

  // ── Step 5: left column — date / time ─────────────────────────────────────
  const txtX = bx + 8;
  const cTop = by + bh - hh - 12;

  lastPage.drawText('Signed On:', {
    x: txtX,
    y: cTop,
    size: 6,
    color: rgb(0.45, 0.45, 0.45),
  });
  lastPage.drawText(formattedDate, {
    x: txtX,
    y: cTop - 12,
    size: 8,
    color: rgb(0.08, 0.08, 0.08),
  });
  lastPage.drawText(formattedTime, {
    x: txtX,
    y: cTop - 24,
    size: 7.5,
    color: rgb(0.122, 0.416, 0.655),
  });

  // ── Step 6: vertical divider ──────────────────────────────────────────────
  const divX = bx + 80;
  lastPage.drawLine({
    start: { x: divX, y: by + 8 },
    end:   { x: divX, y: by + bh - hh - 4 },
    thickness: 0.6,
    color: rgb(0.78, 0.84, 0.92),
  });

  // ── Step 7: signature image — large, right column ────────────────────────
  const sigAreaX = divX + 5;
  const sigAreaW = bw - (divX - bx) - 10;
  const sigAreaH = bh - hh - 12;

  const sigScale = Math.min(
    sigAreaW / signatureImage.width,
    sigAreaH / signatureImage.height,
    0.6
  );
  const sigW = signatureImage.width * sigScale;
  const sigH = signatureImage.height * sigScale;
  const signatureX = sigAreaX + (sigAreaW - sigW) / 2;
  const signatureY = by + 10 + (sigAreaH - sigH) / 2;

  lastPage.drawImage(signatureImage, {
    x: signatureX,
    y: signatureY,
    width: sigW,
    height: sigH,
  });

  // ── Step 8: "Signature" label ─────────────────────────────────────────────
  lastPage.drawText('— Signature —', {
    x: signatureX + sigW / 2 - 16,
    y: by + 3,
    size: 5,
    color: rgb(0.5, 0.5, 0.55),
  });

  // Save the modified PDF
  const signedPdfBytes = await pdfDoc.save();
  
  return new Blob([signedPdfBytes as any], { type: 'application/pdf' });
};

export type { BiltyData };
